
import { useState } from "react";
import { Search, SlidersHorizontal, X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { OptionsList } from "@/components/ui/options-list";
import { OPERATORS, OperatorPicker, ValuePicker } from "@/components/ui/filter-pickers";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export type FilterType = "enum" | "text" | "date" | "number" | "relation";

export interface FilterFieldDefinition {
  id: string;
  label: string;
  icon?: React.ReactNode;
  type: FilterType;
  options?: { value: string; label: string }[];
}

export interface FilterValue {
  fieldId: string;
  operator?: string;
  values: string[];
}

interface FilterBarProps {
  filterFields: FilterFieldDefinition[];
  filters: FilterValue[];
  onFiltersChange: (filters: FilterValue[]) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  action?: React.ReactNode;
  columns?: React.ReactNode;
  trailing?: React.ReactNode;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const DEFAULT_OPERATOR: Record<FilterType, string> = {
  enum: "is",
  relation: "is",
  text: "contains",
  number: "eq",
  date: "is",
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

export function getFilterDisplayValue(
  field: FilterFieldDefinition,
  filter: FilterValue
): string {
  const operator = filter.operator ?? DEFAULT_OPERATOR[field.type];
  if (filter.values.length === 0) return "any";

  if (field.type === "number") {
    if (operator === "between" && filter.values.length >= 2)
      return `${filter.values[0]} – ${filter.values[1]}`;
    return filter.values[0];
  }

  if (field.type === "date") {
    if (operator === "between" && filter.values.length >= 2)
      return `${filter.values[0]} – ${filter.values[1]}`;
    const presetLabels: Record<string, string> = {
      today: "Today",
      this_week: "This week",
      this_month: "This month",
      overdue: "Overdue",
    };
    return presetLabels[filter.values[0]] ?? filter.values[0];
  }

  if (field.type === "text") return filter.values[0];

  const labels = filter.values
    .map((v) => field.options?.find((o) => o.value === v)?.label ?? v)
    .join(", ");
  return labels.length > 24 ? `${filter.values.length} selected` : labels;
}

function resetValuesOnOperatorChange(
  field: FilterFieldDefinition,
  currentOp: string,
  nextOp: string,
  currentValues: string[]
): string[] {
  if (nextOp === "between" && currentOp !== "between") return [];
  if (currentOp === "between" && nextOp !== "between")
    return currentValues.length > 0 ? [currentValues[0]] : [];
  if (field.type === "date" && (currentOp === "is") !== (nextOp === "is")) return [];
  return currentValues;
}

// ─── Desktop: Filter Chip ─────────────────────────────────────────────────────
// open/onOpenChange are controlled by FilterBar so only one popover is open
// at a time, preventing the "second popover disappears" race on pointer events.

function FilterChip({
  field,
  filter,
  open,
  onOpenChange,
  onUpdate,
  onRemove,
}: {
  field: FilterFieldDefinition;
  filter: FilterValue;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (values: string[], operator?: string) => void;
  onRemove: () => void;
}) {
  const operator = filter.operator ?? DEFAULT_OPERATOR[field.type];
  const operatorLabel = OPERATORS[field.type]?.find((o) => o.value === operator)?.label;

  const handleOperatorChange = (newOp: string) => {
    const newValues = resetValuesOnOperatorChange(field, operator, newOp, filter.values);
    onUpdate(newValues, newOp);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium",
            "bg-accent/50 hover:bg-accent transition-colors cursor-pointer shrink-0 h-8"
          )}
        >
          <span className="text-muted-foreground">{field.label}</span>
          {operatorLabel && (
            <>
              <span className="font-medium">·</span>
              <span className="text-muted-foreground italic">{operatorLabel}</span>
            </>
          )}
          <span className="font-medium">·</span>
          <span className="max-w-[120px] truncate">{getFilterDisplayValue(field, filter)}</span>
          <X
            className="h-3 w-3 ml-0.5 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <OperatorPicker
          type={field.type}
          operator={operator}
          onOperatorChange={handleOperatorChange}
        />
        <ValuePicker
          field={field}
          operator={operator}
          selectedValues={filter.values}
          onValuesChange={(v) => onUpdate(v, operator)}
        />
      </PopoverContent>
    </Popover>
  );
}

// ─── Desktop: Filter Menu Button ──────────────────────────────────────────────

function FilterMenuButton({
  availableFields,
  activeCount,
  open,
  onOpenChange,
  onAddFilter,
}: {
  availableFields: FilterFieldDefinition[];
  activeCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddFilter: (fieldId: string) => void;
}) {
  if (availableFields.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 px-3 shrink-0">
          <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
          Filter
          {activeCount > 0 && (
            <span className="ml-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-48 p-0 data-[state=closed]:duration-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <OptionsList
          options={availableFields.map((f) => ({ value: f.id, label: f.label }))}
          selectedValues={[]}
          onSelect={(fieldId) => {
            onAddFilter(fieldId);
          }}
          onEscape={() => onOpenChange(false)}
          searchPlaceholder="Search filters..."
          emptyText="No filters found."
        />
      </PopoverContent>
    </Popover>
  );
}

// ─── Mobile: Filter Sheet ─────────────────────────────────────────────────────
// All filter editing happens inside a bottom Sheet — no nested popovers,
// no pointer-event races, native scroll on touch devices.

function MobileFilterSheet({
  filterFields,
  filters,
  onFiltersChange,
  open,
  onOpenChange,
}: {
  filterFields: FilterFieldDefinition[];
  filters: FilterValue[];
  onFiltersChange: (filters: FilterValue[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [showAddPicker, setShowAddPicker] = useState(false);

  const activeFieldIds = new Set(filters.map((f) => f.fieldId));
  const availableFields = filterFields.filter((f) => !activeFieldIds.has(f.id));
  const activeCount = filters.length;

  const updateFilter = (fieldId: string, values: string[], operator?: string) => {
    onFiltersChange(
      filters.map((f) =>
        f.fieldId === fieldId
          ? { ...f, values, ...(operator !== undefined ? { operator } : {}) }
          : f
      )
    );
  };

  const removeFilter = (fieldId: string) => {
    onFiltersChange(filters.filter((f) => f.fieldId !== fieldId));
  };

  const addFilter = (fieldId: string) => {
    const field = filterFields.find((f) => f.id === fieldId);
    const operator = field ? DEFAULT_OPERATOR[field.type] : "is";
    onFiltersChange([...filters, { fieldId, operator, values: [] }]);
    setShowAddPicker(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        className="relative h-10 w-10 shrink-0"
        aria-label={activeCount > 0 ? `Filters (${activeCount})` : "Filters"}
        onClick={() => onOpenChange(true)}
      >
        <SlidersHorizontal className="h-4 w-4" />
        {activeCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
            {activeCount}
          </span>
        )}
      </Button>

      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next) setShowAddPicker(false);
          onOpenChange(next);
        }}
      >
        <SheetContent
          side="bottom"
          showCloseButton
          className="max-h-[85dvh] gap-0 p-0 flex flex-col"
        >
          <SheetHeader className="px-4 pt-5 pb-3 border-b shrink-0">
            <SheetTitle>
              Filters{activeCount > 0 ? ` (${activeCount})` : ""}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto min-h-0">
            {/* Active filters — each fully expanded, no nested popovers */}
            {filters.length > 0 && (
              <div className="divide-y">
                {filters.map((filter) => {
                  const field = filterFields.find((f) => f.id === filter.fieldId);
                  if (!field) return null;
                  const operator = filter.operator ?? DEFAULT_OPERATOR[field.type];

                  const handleOperatorChange = (newOp: string) => {
                    const newValues = resetValuesOnOperatorChange(
                      field,
                      operator,
                      newOp,
                      filter.values
                    );
                    updateFilter(filter.fieldId, newValues, newOp);
                  };

                  return (
                    <div key={filter.fieldId} className="py-3">
                      <div className="flex items-center justify-between px-4 mb-2">
                        <span className="text-sm font-medium">{field.label}</span>
                        <button
                          onClick={() => removeFilter(filter.fieldId)}
                          className="p-1 -mr-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <OperatorPicker
                        type={field.type}
                        operator={operator}
                        onOperatorChange={handleOperatorChange}
                      />
                      <ValuePicker
                        field={field}
                        operator={operator}
                        selectedValues={filter.values}
                        onValuesChange={(v) => updateFilter(filter.fieldId, v, operator)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add filter */}
            {availableFields.length > 0 && (
              <div className={cn("border-t py-3", filters.length === 0 && "border-t-0")}>
                {showAddPicker ? (
                  <div>
                    <div className="flex items-center justify-between px-4 mb-2">
                      <span className="text-sm font-medium">Add filter</span>
                      <button
                        onClick={() => setShowAddPicker(false)}
                        className="p-1 -mr-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <OptionsList
                      options={availableFields.map((f) => ({
                        value: f.id,
                        label: f.label,
                      }))}
                      selectedValues={[]}
                      onSelect={addFilter}
                      onEscape={() => setShowAddPicker(false)}
                      searchPlaceholder="Search filters..."
                      emptyText="No filters available."
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddPicker(true)}
                    className="flex items-center gap-2 px-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add filter
                  </button>
                )}
              </div>
            )}

            {filters.length === 0 && !showAddPicker && availableFields.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                No filters available.
              </p>
            )}
          </div>

          <SheetFooter className="shrink-0 border-t px-4 py-3 flex-row gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              disabled={activeCount === 0}
              onClick={() => {
                onFiltersChange([]);
                onOpenChange(false);
              }}
            >
              Clear all
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Done
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ─── Main FilterBar ───────────────────────────────────────────────────────────

export function FilterBar({
  filterFields,
  filters,
  onFiltersChange,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  action,
  columns,
  trailing,
}: FilterBarProps) {
  // Only one desktop popover open at a time to prevent pointer-event races
  const [activePopoverId, setActivePopoverId] = useState<string | null>(null);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const activeFieldIds = new Set(filters.map((f) => f.fieldId));
  const availableFields = filterFields.filter((f) => !activeFieldIds.has(f.id));
  const hasActiveFilters = filters.length > 0 || searchValue.length > 0;

  const updateFilter = (fieldId: string, values: string[], operator?: string) => {
    const existing = filters.find((f) => f.fieldId === fieldId);
    const operatorChanged = operator !== undefined && operator !== existing?.operator;
    if (values.length === 0 && !operatorChanged) {
      onFiltersChange(filters.filter((f) => f.fieldId !== fieldId));
    } else {
      onFiltersChange(
        filters.map((f) =>
          f.fieldId === fieldId
            ? { ...f, values, ...(operator !== undefined ? { operator } : {}) }
            : f
        )
      );
    }
  };

  const removeFilter = (fieldId: string) => {
    onFiltersChange(filters.filter((f) => f.fieldId !== fieldId));
  };

  const addFilter = (fieldId: string) => {
    const field = filterFields.find((f) => f.id === fieldId);
    const operator = field ? DEFAULT_OPERATOR[field.type] : "is";
    onFiltersChange([...filters, { fieldId, operator, values: [] }]);
    // Defer opening so Radix DismissableLayer doesn't catch the same
    // pointer event as an "outside click" and immediately close the chip popover.
    setTimeout(() => setActivePopoverId(fieldId), 0);
  };

  const clearAll = () => {
    onFiltersChange([]);
    onSearchChange("");
  };

  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center">
      <div className="flex min-w-0 items-center gap-2">
        {/* Search */}
        <div className="relative min-w-0 flex-1 md:w-64 md:flex-none">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-9 pl-8 text-sm"
          />
        </div>

        {/* Mobile: Sheet-based filter trigger (no nested popovers) */}
        {!isDesktop && (
          <MobileFilterSheet
            filterFields={filterFields}
            filters={filters}
            onFiltersChange={onFiltersChange}
            open={mobileSheetOpen}
            onOpenChange={setMobileSheetOpen}
          />
        )}

        {/* Desktop: Filter menu, then Columns, then active chips */}
        {isDesktop && (
          <>
            <FilterMenuButton
              availableFields={availableFields}
              activeCount={filters.length}
              open={activePopoverId === "__add__"}
              onOpenChange={(open) =>
                setActivePopoverId((prev) =>
                  open ? "__add__" : prev === "__add__" ? null : prev
                )
              }
              onAddFilter={addFilter}
            />

            {columns && <div className="shrink-0">{columns}</div>}

            <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto scrollbar-none">
              {filters.map((filter) => {
                const field = filterFields.find((f) => f.id === filter.fieldId);
                if (!field) return null;
                return (
                  <FilterChip
                    key={filter.fieldId}
                    field={field}
                    filter={filter}
                    open={activePopoverId === filter.fieldId}
                    onOpenChange={(open) =>
                      setActivePopoverId((prev) =>
                        open ? filter.fieldId : prev === filter.fieldId ? null : prev
                      )
                    }
                    onUpdate={(values, operator) =>
                      updateFilter(filter.fieldId, values, operator)
                    }
                    onRemove={() => removeFilter(filter.fieldId)}
                  />
                );
              })}
            </div>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground shrink-0"
                onClick={clearAll}
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}

            {trailing && <div className="ml-auto shrink-0">{trailing}</div>}
          </>
        )}

        {!isDesktop && action && <div className="shrink-0">{action}</div>}
      </div>

      {/* Mobile: active chips below the search row */}
      {!isDesktop && filters.length > 0 && (
        <div
          data-testid="mobile-filter-chips"
          className="flex items-center gap-1.5 overflow-x-auto scrollbar-none"
        >
          {filters.map((filter) => {
            const field = filterFields.find((f) => f.id === filter.fieldId);
            if (!field) return null;
            return (
              <span
                key={filter.fieldId}
                className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border bg-muted/50 px-2 text-xs font-medium"
              >
                <span className="text-muted-foreground">{field.label}</span>
                <span>·</span>
                <span className="max-w-[120px] truncate">
                  {getFilterDisplayValue(field, filter)}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${field.label} filter`}
                  className="ml-0.5 text-muted-foreground hover:text-foreground"
                  onClick={() => removeFilter(filter.fieldId)}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
